import React from 'react';

function MetricsCards({ vehicle }) {
  const reliability = vehicle.reliability_score || 0;
  const mttf = vehicle.mttf || 'N/A';
  const risk1yr = vehicle.predictive_risk?.risk_1yr;
  const complaints = vehicle.total_complaints || vehicle.complaint_count || 0;
  const history = vehicle.history || [];

  const safetyIncidents = history.filter(h => h.crash || h.fire).length;
  const injuries = history.reduce((sum, h) => sum + (h.numberOfInjuries || 0), 0);
  const deaths = history.reduce((sum, h) => sum + (h.numberOfDeaths || 0), 0);

  // Find primary component
  const componentCounts = {};
  history.forEach(h => {
    const comp = (h.components || 'Unknown').split(',')[0].trim();
    componentCounts[comp] = (componentCounts[comp] || 0) + 1;
  });
  const primaryComponent = Object.entries(componentCounts).sort((a, b) => b[1] - a[1])[0];

  // Latest report date
  const dates = history.map(h => h.dateOfIncident || h.dateComplaintFiled).filter(Boolean).sort();
  const latestReport = dates.length > 0 ? dates[dates.length - 1] : 'N/A';

  const getReliabilityLabel = (score) => {
    if (score >= 80) return 'Good';
    if (score >= 60) return 'Fair';
    if (score >= 40) return 'Below Average';
    return 'Poor';
  };

  const getRiskLabel = (risk) => {
    if (risk == null) return 'N/A';
    if (risk < 15) return 'Low risk';
    if (risk < 40) return 'Moderate risk';
    return 'High risk';
  };

  return (
    <div>
      {/* Top stats bar */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'16px',marginBottom:'20px'}}>
        <div style={{background:'#f8fafc',borderRadius:'10px',padding:'14px',borderLeft:'4px solid #6366f1'}}>
          <div style={{fontSize:'11px',textTransform:'uppercase',color:'#6366f1',fontWeight:'600',letterSpacing:'.5px'}}>VIN</div>
          <div style={{fontSize:'15px',fontWeight:'700',color:'#0f172a',marginTop:'4px',wordBreak:'break-all'}}>{vehicle.vin}</div>
        </div>
        <div style={{background:'#f8fafc',borderRadius:'10px',padding:'14px',borderLeft:'4px solid #f59e0b'}}>
          <div style={{fontSize:'11px',textTransform:'uppercase',color:'#f59e0b',fontWeight:'600',letterSpacing:'.5px'}}>Total Complaints</div>
          <div style={{fontSize:'24px',fontWeight:'700',color:'#0f172a',marginTop:'4px'}}>{complaints}</div>
        </div>
        <div style={{background:'#f8fafc',borderRadius:'10px',padding:'14px',borderLeft:'4px solid #8b5cf6'}}>
          <div style={{fontSize:'11px',textTransform:'uppercase',color:'#8b5cf6',fontWeight:'600',letterSpacing:'.5px'}}>Primary Component</div>
          <div style={{fontSize:'14px',fontWeight:'700',color:'#0f172a',marginTop:'4px'}}>{primaryComponent ? primaryComponent[0] : 'N/A'}</div>
        </div>
        <div style={{background:'#f8fafc',borderRadius:'10px',padding:'14px',borderLeft:'4px solid #64748b'}}>
          <div style={{fontSize:'11px',textTransform:'uppercase',color:'#64748b',fontWeight:'600',letterSpacing:'.5px'}}>Latest Report</div>
          <div style={{fontSize:'15px',fontWeight:'700',color:'#0f172a',marginTop:'4px'}}>{latestReport}</div>
        </div>
      </div>

      {/* Colored metric cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'16px',marginBottom:'20px'}}>
        {/* Safety Incidents */}
        <div style={{background:'linear-gradient(135deg,#ef4444,#dc2626)',borderRadius:'12px',padding:'20px',color:'#fff'}}>
          <div style={{fontSize:'12px',fontWeight:'600',opacity:'.85',marginBottom:'4px'}}>⚠️ Safety Incidents</div>
          <div style={{fontSize:'36px',fontWeight:'800'}}>{safetyIncidents}</div>
          <div style={{fontSize:'12px',opacity:'.75',marginTop:'4px'}}>{deaths} deaths, {injuries} injuries, {history.filter(h=>h.crash).length} crashes</div>
        </div>

        {/* MTTF */}
        <div style={{background:'linear-gradient(135deg,#22c55e,#16a34a)',borderRadius:'12px',padding:'20px',color:'#fff'}}>
          <div style={{fontSize:'12px',fontWeight:'600',opacity:'.85',marginBottom:'4px'}}>⏱️ MTTF (Years)</div>
          <div style={{fontSize:'36px',fontWeight:'800'}}>{mttf}</div>
          <div style={{fontSize:'12px',opacity:'.75',marginTop:'4px'}}>Mean time to failure</div>
        </div>

        {/* Risk Score */}
        <div style={{background:'linear-gradient(135deg,#f97316,#ea580c)',borderRadius:'12px',padding:'20px',color:'#fff'}}>
          <div style={{fontSize:'12px',fontWeight:'600',opacity:'.85',marginBottom:'4px'}}>🛡️ Reliability Score</div>
          <div style={{fontSize:'36px',fontWeight:'800'}}>{reliability}%</div>
          <div style={{fontSize:'12px',opacity:'.75',marginTop:'4px'}}>{getReliabilityLabel(reliability)}</div>
        </div>
      </div>

      {/* Comparative Analysis */}
      {vehicle.comparative && (
        <div style={{background:'#fff',borderRadius:'12px',padding:'20px',boxShadow:'0 1px 3px rgba(0,0,0,.08)',marginBottom:'20px'}}>
          <h3 style={{margin:'0 0 16px',fontSize:'15px',color:'#0f172a'}}>📊 Comparative Analysis</h3>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'16px'}}>
            <div style={{textAlign:'center',padding:'12px',background:'#f8fafc',borderRadius:'8px'}}>
              <div style={{fontSize:'11px',color:'#64748b',textTransform:'uppercase',marginBottom:'4px'}}>Dataset Average</div>
              <div style={{fontSize:'22px',fontWeight:'700',color:'#0f172a'}}>{vehicle.comparative.dataset_avg}</div>
              <div style={{fontSize:'11px',color:'#94a3b8'}}>complaints/VIN</div>
            </div>
            <div style={{textAlign:'center',padding:'12px',background:'#f8fafc',borderRadius:'8px'}}>
              <div style={{fontSize:'11px',color:'#64748b',textTransform:'uppercase',marginBottom:'4px'}}>Same Make Average</div>
              <div style={{fontSize:'22px',fontWeight:'700',color:'#0f172a'}}>{vehicle.comparative.make_avg}</div>
              <div style={{fontSize:'11px',color:'#94a3b8'}}>complaints/VIN</div>
            </div>
            <div style={{textAlign:'center',padding:'12px',background:'#f8fafc',borderRadius:'8px'}}>
              <div style={{fontSize:'11px',color:'#64748b',textTransform:'uppercase',marginBottom:'4px'}}>Same Year Average</div>
              <div style={{fontSize:'22px',fontWeight:'700',color:'#0f172a'}}>{vehicle.comparative.year_avg}</div>
              <div style={{fontSize:'11px',color:'#94a3b8'}}>complaints/VIN</div>
            </div>
          </div>
        </div>
      )}

      {/* Predictive Risk with progress bars */}
      {vehicle.predictive_risk && (
        <div style={{background:'#fff',borderRadius:'12px',padding:'20px',boxShadow:'0 1px 3px rgba(0,0,0,.08)',marginBottom:'20px'}}>
          <h3 style={{margin:'0 0 16px',fontSize:'15px',color:'#0f172a'}}>🔮 Predictive Risk: Component Failure Probability</h3>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'16px'}}>
            {[
              {label:'1 Year Risk', value: vehicle.predictive_risk.risk_1yr},
              {label:'3 Year Risk', value: vehicle.predictive_risk.risk_3yr},
              {label:'5 Year Risk', value: vehicle.predictive_risk.risk_5yr}
            ].map((r, i) => {
              const color = r.value < 30 ? '#22c55e' : r.value < 60 ? '#f59e0b' : '#ef4444';
              return (
                <div key={i} style={{padding:'12px',background:'#f8fafc',borderRadius:'8px'}}>
                  <div style={{fontSize:'12px',color:'#64748b',marginBottom:'8px'}}>{r.label}</div>
                  <div style={{background:'#e2e8f0',borderRadius:'4px',height:'8px',overflow:'hidden',marginBottom:'6px'}}>
                    <div style={{width:`${Math.min(100,r.value)}%`,height:'100%',background:color,borderRadius:'4px',transition:'width .5s ease'}}/>
                  </div>
                  <div style={{fontSize:'18px',fontWeight:'700',color}}>{r.value}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default MetricsCards;
